import {
  Button,
  Icon,
  PageBody,
  PageSection,
  Select,
  SelectOption,
  Snackbar,
  Spacer,
  Stack,
  TextTitle,
  Toggle,
  Tooltip,
} from '@pypestream/design-system';
import { TranslationComponent, useTranslation } from '@pypestream/translations';
import { FC, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader } from '../../../components';
import { DeleteUserConfirmationModal } from '../../../components/modals/delete-user-confirmation-modal/delete-user-confirmation-modal';
import {
  sendManagerEvent,
  useGlobalAppCtxSelector,
  useManagerCtxSelector,
  useManagerStateMatches,
  useManagerStateMatchesOneOf,
  useUserCtxSelector,
  useUserStateMatches,
} from '../../../xstate/app.xstate';
import {
  NEW_USER_TEMP_ID,
  UserDetailsType,
} from '../../../xstate/user-details-xstate-helper';
import { IPage } from '../../types';
import { PermissionsTable } from '../../../components/permissions-table';
import {
  UserProfileDetails,
  UserProfileDetailsFormValues,
  useUserProfileDetailsForm,
} from './user-profile-details';
import {
  ProductNames,
  AnalyticsRoleNames,
  isPypestreamEmployeeEmail,
} from '../../../xstate/permission-details-xstate-helper';
import { useAuthRole, OrganizationRoleNames } from '../../../hooks';
import { resolveUserDetailsTarget } from '../../../utils';
import _ from 'lodash';
import { UserAuthMethods } from './user-auth-methods';
import { ConsentStatusEnum } from '@pypestream/api-services';

function hasContactCenterRole(userDetails: UserDetailsType) {
  return userDetails.orgs.some((org) => {
    const hasDirectRole = org.assignedProjects?.some((project) =>
      project.project.project_product_settings.some(
        (projectProduct) =>
          projectProduct.product.name === ProductNames.AGENT_ASSIST
      )
    );
    const hasTeamRole = org.assignedTeams?.some((team) =>
      team.team.team_accounts.some((teamAccount) =>
        teamAccount.team_account_projects.some((project) =>
          project.project.project_product_settings.some(
            (projectProduct) =>
              projectProduct.product.name === ProductNames.AGENT_ASSIST
          )
        )
      )
    );

    return hasDirectRole || hasTeamRole;
  });
}
type IUserDetailsProps = IPage & {
  isInviteUser?: boolean;
  /**
   * Which user's details to show, when the route does not name one. My Account
   * (PSTUDIO-4095) renders this component on `/organization/:org_id/my-account`
   * and on the org-less `/my-account`, neither of which carries a `user_id`.
   * Omitted on `/organization/:org_id/users/:user_id`, where the route wins.
   */
  userId?: string;
  /** The org whose tree the Roles table shows; same reason as `userId`. */
  orgId?: string;
  /**
   * Render as the standalone My Account surface: the user is looking at their
   * own profile, not at a row of the Users list, so the "All Users" back link
   * (which leads to a page a role-less user is not entitled to) is not shown.
   */
  isMyAccount?: boolean;
};

export const UserDetails: FC<IUserDetailsProps> = ({
  isInviteUser = false,
  userId: userIdProp,
  orgId: orgIdProp,
  isMyAccount = false,
}) => {
  const params = useParams();
  const [t] = useTranslation();
  const navigate = useNavigate();
  const { org_id: routeOrgId, user_id: routeUserId } = params;
  const { orgId: targetOrgId, userId: targetUserId } = resolveUserDetailsTarget(
    {
      routeOrgId,
      routeUserId,
      orgIdProp,
      userIdProp,
      isMyAccount,
    }
  );

  const { allUserDetails, selectedUser } = useManagerCtxSelector((ctx) => ({
    allUserDetails: ctx.userDetails,
    selectedUser: ctx.selectedUser,
  }));
  const { loggedInUserId } = useUserCtxSelector((ctx) => ({
    loggedInUserId: ctx.user?.id,
  }));
  const isCurrentUser = !!loggedInUserId && loggedInUserId === targetUserId;

  const featureFlags = useGlobalAppCtxSelector((ctx) => ctx.featureFlags);

  const userDetails = isInviteUser
    ? allUserDetails[NEW_USER_TEMP_ID]
    : allUserDetails[targetUserId];
  const { state: userDetailsState, userDetailUpdates } = (userDetails &&
    userDetails) || { state: null, userDetailUpdates: null };

  const isSuperAdmin = useAuthRole([OrganizationRoleNames.SUPER_ADMIN]);
  const isViewerDataTeamMember =
    isCurrentUser &&
    !!userDetailsState?.orgs.some((org) =>
      org.assignedProjects?.some((project) =>
        project._account_project_roles.some(
          ({ role }) => role.name === AnalyticsRoleNames.PYPESTREAM_DATA_TEAM
        )
      )
    );
  const isPypestreamDataTeamAssignerEligible =
    isSuperAdmin || isViewerDataTeamMember;

  const isIdle = useManagerStateMatches('orgRelated.ready.userDetails.idle');
  const loadingUserDetails = Boolean(
    useManagerStateMatchesOneOf([
      'orgRelated.ready.userDetails.loading',
      'orgRelated.ready.userDetails.addNewUser.loadingPrerequisites',
      'orgRelated.ready.userDetails.deleteUser.deleting',
    ])
  );

  const showDeleteUserConfirmationModal = useManagerStateMatches(
    'orgRelated.ready.userDetails.deleteUser.confirmUserDeletion'
  );

  const hasInvitedUserState = useManagerStateMatches(
    'orgRelated.ready.userDetails.addNewUser.invited'
  );

  const isDeletingUser = useManagerStateMatches(
    'orgRelated.ready.userDetails.deleteUser.deleting'
  );

  const hasDeletedUserState = useManagerStateMatches(
    'orgRelated.ready.userDetails.deleteUser.deleted'
  );

  // Both navigation effects below leave for the Users route, so neither may
  // fire on My Account (PSTUDIO-4095) — that is the page this surface exists to
  // stop depending on. `deleteUser.deleted` in particular is a state with no
  // automatic exit, so without this guard entering My Account shortly after
  // deleting a user would bounce straight out of it.
  useEffect(() => {
    // TODO: Find a way to navigate user from xstate directly instead of waiting on one full re-render
    // Once user is invited, navigate to user details page
    if (hasDeletedUserState && !isMyAccount) {
      navigate(`/organization/${targetOrgId}/users`);
    }
  }, [hasDeletedUserState, isMyAccount, navigate, targetOrgId]);

  useEffect(() => {
    // TODO: Find a way to navigate user from xstate directly instead of waiting on one full re-render
    // Once user is invited, navigate to user details page
    if (hasInvitedUserState && selectedUser && !isMyAccount) {
      navigate(`/organization/${targetOrgId}/users/${selectedUser}`);
    }
  }, [hasInvitedUserState, isMyAccount, navigate, targetOrgId, selectedUser]);

  const hasErrors = useManagerStateMatches(
    'orgRelated.ready.userDetails.error'
  );

  const savingUpdates = Boolean(
    useManagerStateMatchesOneOf([
      'orgRelated.ready.userDetails.saveUpdates',
      'orgRelated.ready.userDetails.addNewUser.invitingUser',
    ])
  );

  const isReloadingCurrentUser = useUserStateMatches('loggedIn.loading');

  const addingTeamToOrg = useManagerStateMatches(
    'orgRelated.ready.userDetails.addTeamToOrg'
  );

  const isGoogleProvider = !!userDetails?.state?.userAuthMethods?.some(
    (method) => method.providerId === 'google' || method.isCustom
  );

  const formValues = useMemo<UserProfileDetailsFormValues>(
    () => ({
      firstName: userDetails?.state?.firstName || '',
      lastName: userDetails?.state?.lastName || '',
      email: userDetails?.state?.userName || '',
      optionalConsentStatus:
        userDetails?.state?.optionalConsentStatus || ConsentStatusEnum.Unknown,
      requiredConsentStatus: userDetails?.state?.requiredConsentStatus,
      jobTitle: userDetails?.state?.settings?.jobTitle || '',
      defaultLanguage: userDetails?.state?.settings?.defaultLanguage || '',
      defaultTimezone: userDetails?.state?.settings?.defaultTimezone || '',
      profilePhoto: isGoogleProvider
        ? userDetails?.state?.picture
        : userDetails?.state?.files?.[0]?.url,
      userIconId: undefined,
    }),
    [
      userDetails?.state?.firstName,
      userDetails?.state?.lastName,
      userDetails?.state?.userName,
      userDetails?.state?.optionalConsentStatus,
      userDetails?.state?.requiredConsentStatus,
      userDetails?.state?.settings?.jobTitle,
      userDetails?.state?.settings?.defaultLanguage,
      userDetails?.state?.settings?.defaultTimezone,
      userDetails?.state?.picture,
      userDetails?.state?.files,
      isGoogleProvider,
    ]
  );

  const {
    form,
    getValues: getUserProfileDetailsValues,
    trigger: triggerUserProfileDetailsForm,
    reset: resetUserDetailsForm,
    watchedValues: watchedUserProfileDetailsValues,
  } = useUserProfileDetailsForm({
    formValues,
  });

  if (hasErrors) {
    return (
      <PageBody>
        <TextTitle size="small" i18nKey="manager/common:genericError">
          Something went wrong: Try again
        </TextTitle>
      </PageBody>
    );
  }

  if (!userDetailsState) {
    if (loadingUserDetails || isIdle) {
      return (
        <PageBody background="none">
          <PageSection>
            <Loader relative />
          </PageSection>
        </PageBody>
      );
    }
    return null;
  }

  return (
    <PageBody background="none" width="large">
      {!isMyAccount && (
        <>
          <Button
            size="small"
            variant="ghost"
            test-id="user-details-all-users-button"
            onClick={() => navigate(`/organization/${targetOrgId}/users`)}
          >
            <Icon slot="prefix" name="chevron-left" />
            All Users
          </Button>
          <Spacer size="2xsmall" />
        </>
      )}
      {/*
        My Account is reached from a menu item labelled "My Account", but the
        heading here is the user's own name, so nothing on the page confirms
        where you are — and the "All Users" link that used to sit above it is
        gone on this surface. Name it. `manager/preferences:title` already
        carries the string in every locale (it survives from the standalone
        page MNGR-864 folded into user-details).
      */}
      {isMyAccount && (
        <>
          <TextTitle
            test-id="my-account-title"
            i18nKey="manager/preferences:title"
          >
            My Account
          </TextTitle>
          <Spacer size="2xsmall" />
        </>
      )}
      <TextTitle
        test-id="user-details-subtitle"
        size={isMyAccount ? 'xsmall' : undefined}
      >
        {(isInviteUser && (
          <TranslationComponent i18nKey="manager/users:usersList.inviteUser">
            Invite User
          </TranslationComponent>
        )) ||
          `${userDetailsState.firstName} ${userDetailsState.lastName}`}
      </TextTitle>
      <Spacer size="large" />
      <>
        <PageSection size="small">
          <UserProfileDetails
            form={form}
            isInviteUser={isInviteUser}
            canManageAccess={userDetailsState.canManageAccess || isCurrentUser}
            formValues={formValues}
            watchedValues={watchedUserProfileDetailsValues}
            isGoogleProvider={isGoogleProvider}
            isCurrentUser={isCurrentUser}
            orgId={targetOrgId}
          />
        </PageSection>
        <PageSection size="small">
          <Stack alignItems="center" justifyContent="space-between">
            <TextTitle
              test-id="permissions-table"
              size="xsmall"
              i18nKey="manager/users:userDetails.permissionAndAccessTitle"
            >
              Roles
            </TextTitle>
            {isCurrentUser && (
              <Stack alignItems="center" gutter="xsmall">
                <Tooltip
                  content={
                    t('manager/users:userDetails.smartOrgMenuTooltip') || ''
                  }
                >
                  <Icon name="info" size="xsmall" color="gray" />
                </Tooltip>
                <Toggle
                  textPosition="left"
                  checked={userDetailsState.settings?.smartOrgMenuEnabled}
                  onChange={(e) => {
                    sendManagerEvent({
                      type: 'manager.userDetails.updateUserSettings',
                      settings: {
                        smartOrgMenuEnabled: e.target.checked,
                      },
                    });
                  }}
                >
                  <TranslationComponent i18nKey="manager/users:userDetails.smartOrgMenu">
                    Smart Org Menu
                  </TranslationComponent>
                </Toggle>
              </Stack>
            )}
          </Stack>
          <Spacer size="large" />
          <PermissionsTable
            orgs={userDetailsState.orgs}
            canManageAccess={userDetailsState.canManageAccess}
            isSavingDetails={savingUpdates || isReloadingCurrentUser}
            isPypestreamDataTeamEligible={
              isPypestreamDataTeamAssignerEligible &&
              isPypestreamEmployeeEmail(watchedUserProfileDetailsValues.email)
            }
            isAddingTeamToOrg={addingTeamToOrg}
            handleAllOrgsCollapse={({ collapse }) => {
              sendManagerEvent({
                type: 'manager.userDetails.toggleAllOrgCollapseState',
                collapse,
              });
            }}
            handleOrgCollapse={({ orgId, collapse }) => {
              sendManagerEvent({
                type: 'manager.userDetails.toggleOrgCollapseState',
                orgId,
                collapse,
              });
            }}
            handleAddProject={({ project }) => {
              sendManagerEvent({
                type: 'manager.userDetails.addProject',
                project,
              });
            }}
            handleAddTeam={({ orgId, team }) => {
              sendManagerEvent({
                type: 'manager.userDetails.addTeam',
                orgId,
                team,
              });
            }}
            handleRemoveTeam={({ orgId, team }) => {
              sendManagerEvent({
                type: 'manager.userDetails.removeTeam',
                orgId,
                team,
              });
            }}
            handleUpdateManagerRole={({ orgId, role, operation }) => {
              sendManagerEvent({
                type: 'manager.userDetails.updateManagerRole',
                role,
                orgId,
                operation,
              });
            }}
            handleUpdateProjectProductRole={({
              orgId,
              projectId,
              role,
              operation,
            }) => {
              sendManagerEvent({
                type: 'manager.userDetails.updateProjectProductRole',
                orgId,
                projectId,
                role,
                operation,
              });
            }}
            handleRemoveProjectWithRoles={({ orgId, projectId }) => {
              sendManagerEvent({
                type: 'manager.userDetails.removeProjectWithRoles',
                orgId,
                projectId,
              });
            }}
          />
          <Snackbar
            id="snackbar"
            open={userDetailUpdates.hasPendingUpdates || form.formState.isDirty}
          >
            {isInviteUser
              ? 'When you are ready, invite the user'
              : 'When you are ready, save your changes'}
            <Button
              size="medium"
              variant="secondary"
              slot="action"
              onClick={() => {
                resetUserDetailsForm();
                if (isInviteUser) {
                  sendManagerEvent({
                    type: 'manager.userDetails.discardUpdates',
                    userId: NEW_USER_TEMP_ID,
                  });
                } else {
                  sendManagerEvent({
                    type: 'manager.userDetails.discardUpdates',
                    userId: targetUserId,
                  });
                }
              }}
              disabled={savingUpdates}
              test-id="user-details-cancel-button"
            >
              Cancel
            </Button>
            <Button
              size="medium"
              variant="primary"
              slot="action"
              onClick={async () => {
                const isUserDetailsValid =
                  await triggerUserProfileDetailsForm();
                if (!isUserDetailsValid) return;

                if (isInviteUser) {
                  sendManagerEvent({
                    type: 'manager.userDetails.inviteUser',
                    orgId: targetOrgId,
                    form: _.pick(getUserProfileDetailsValues(), [
                      'firstName',
                      'lastName',
                      'email',
                      'jobTitle',
                    ]),
                  });
                } else {
                  sendManagerEvent({
                    type: 'manager.userDetails.saveUpdates',
                    userId: targetUserId,
                    orgId: targetOrgId,
                    form: _.pick(getUserProfileDetailsValues(), [
                      'firstName',
                      'lastName',
                      'optionalConsentStatus',
                      'userIconId',
                      'profilePhoto',
                      'jobTitle',
                      'defaultLanguage',
                      'defaultTimezone',
                    ]),
                  });
                }
              }}
              disabled={savingUpdates}
              test-id="user-details-save-button"
            >
              {(isInviteUser &&
                (savingUpdates ? 'Sending Invite...' : 'Send Invitation')) ||
                (savingUpdates ? 'Saving...' : 'Save')}
            </Button>
          </Snackbar>
        </PageSection>
        {hasContactCenterRole(userDetailsState) && (
          <PageSection size="small">
            <Stack alignItems="center" justifyContent="space-between">
              <Stack alignItems="center" gutter="xsmall">
                <TextTitle
                  size="xsmall"
                  i18nKey="manager/users:userDetails.agentConversationLimit"
                >
                  Agent Conversation Limit
                </TextTitle>
                <Tooltip
                  content={
                    t(
                      'manager/users:userDetails.agentConversationLimitTooltip'
                    ) || ''
                  }
                >
                  <Icon name="info" size="small" color="gray" />
                </Tooltip>
              </Stack>
              <Select
                value={
                  userDetailsState.settings?.agentConversationLimit?.toString() ||
                  ''
                }
                variant="outlined"
                disabled={!userDetailsState.canManageAccess && !isCurrentUser}
                onChange={(e) => {
                  sendManagerEvent({
                    type: 'manager.userDetails.updateUserSettings',
                    settings: {
                      agentConversationLimit: e.target.value
                        ? Number(e.target.value)
                        : null,
                    },
                  });
                }}
              >
                <>
                  {new Array(10).fill(null).map((val, index) => (
                    <SelectOption value={(index + 1).toString()} key={index}>
                      {index + 1}
                    </SelectOption>
                  ))}
                </>
                <SelectOption value="">No limit</SelectOption>
              </Select>
            </Stack>
          </PageSection>
        )}
        {!isInviteUser && (
          <PageSection size="small">
            <TextTitle size="xsmall">Login Methods</TextTitle>
            <Spacer size="large" />
            <UserAuthMethods
              userDetailsState={userDetailsState}
              orgId={targetOrgId}
              isCurrentUser={isCurrentUser}
            />
          </PageSection>
        )}
        {userDetailsState.canManageAccess &&
          !isInviteUser &&
          !isCurrentUser && (
            <Button
              size="large"
              variant="danger"
              disabled={loadingUserDetails}
              onClick={() =>
                sendManagerEvent({
                  type: 'manager.userDetails.deleteUser.openConfirmation',
                })
              }
            >
              Delete User
            </Button>
          )}
      </>
      <DeleteUserConfirmationModal
        userName={`${userDetailsState.firstName} ${userDetailsState.lastName}`}
        open={showDeleteUserConfirmationModal || isDeletingUser}
        onClose={() =>
          sendManagerEvent({
            type: 'manager.userDetails.setStateToIdle',
          })
        }
        onDelete={() => {
          sendManagerEvent({
            type: 'manager.userDetails.deleteUser.confirmed',
            userId: targetUserId,
            orgId: targetOrgId,
          });
        }}
        isDeletingUser={isDeletingUser}
      />
    </PageBody>
  );
};
